import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Clock,
  AlertCircle,
  Users,
  Target,
} from 'lucide-react';
import clsx from 'clsx';

export function Analytics() {
  const { projectId } = useParams<{ projectId: string }>();
  const api = useApi();

  const { data: workItems, isLoading: loadingItems } = useQuery({
    queryKey: ['workItems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.getMembers('ws-1'),
  });

  if (loadingItems) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Calculate statistics
  const totalIssues = workItems?.length || 0;
  const completedIssues = workItems?.filter((wi: any) => {
    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
    return state?.bh_group === 4; // Completed group
  }).length || 0;
  const inProgressIssues = workItems?.filter((wi: any) => {
    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
    return state?.bh_group === 3; // Started group
  }).length || 0;

  const completionRate = totalIssues > 0 ? Math.round((completedIssues / totalIssues) * 100) : 0;

  // Issues by priority
  const priorityLabels = ['None', 'Low', 'Medium', 'High', 'Urgent'];
  const priorityCounts = [0, 1, 2, 3, 4].map(p =>
    workItems?.filter((wi: any) => wi.bh_priority === p).length || 0
  );
  const maxPriorityCount = Math.max(...priorityCounts, 1);

  // Issues by state
  const stateData = states?.map((state: any) => ({
    name: state.bh_name,
    color: state.bh_color,
    count: workItems?.filter((wi: any) => wi._bh_state_value === state.bh_stateid).length || 0,
  })) || [];
  const maxStateCount = Math.max(...stateData.map((s: any) => s.count), 1);

  // Issues by assignee
  const assigneeData = members?.map((member: any) => ({
    name: member.bh_name,
    count: workItems?.filter((wi: any) => wi.bh_assignee === member.bh_memberid).length || 0,
  })) || [];
  const unassigned = workItems?.filter((wi: any) => !wi.bh_assignee).length || 0;
  if (unassigned > 0) {
    assigneeData.push({ name: 'Unassigned', count: unassigned });
  }
  const maxAssigneeCount = Math.max(...assigneeData.map((a: any) => a.count), 1);

  // Overdue issues
  const today = new Date().toISOString().split('T')[0];
  const overdueIssues = workItems?.filter((wi: any) => {
    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
    return wi.bh_targetdate && wi.bh_targetdate < today && state?.bh_group !== 4;
  }).length || 0;

  // Total estimate points
  const totalEstimate = workItems?.reduce((sum: number, wi: any) => sum + (wi.bh_estimate || 0), 0) || 0;
  const completedEstimate = workItems?.filter((wi: any) => {
    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
    return state?.bh_group === 4;
  }).reduce((sum: number, wi: any) => sum + (wi.bh_estimate || 0), 0) || 0;

  // Current cycle stats
  const currentCycle = cycles?.find((c: any) => c.bh_status === 'current');
  const cycleIssues = currentCycle
    ? workItems?.filter((wi: any) => wi.bh_cycle === currentCycle.bh_cycleid)
    : [];
  const cycleCompleted = cycleIssues?.filter((wi: any) => {
    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
    return state?.bh_group === 4;
  }).length || 0;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-8 h-8 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Issues</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalIssues}</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{completedIssues}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-green-600 font-medium">{completionRate}%</span>
              <span className="text-gray-500">completion rate</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{inProgressIssues}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{overdueIssues}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            {overdueIssues > 0 && (
              <div className="mt-3 flex items-center gap-1 text-sm">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span className="text-red-600">Needs attention</span>
              </div>
            )}
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Issues by State */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Issues by State</h3>
            <div className="space-y-3">
              {stateData.map((state: any) => (
                <div key={state.name} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-gray-600 truncate">{state.name}</div>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full rounded-lg transition-all"
                      style={{
                        width: `${(state.count / maxStateCount) * 100}%`,
                        backgroundColor: state.color,
                      }}
                    />
                  </div>
                  <div className="w-8 text-sm font-medium text-gray-900 text-right">{state.count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Issues by Priority */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Issues by Priority</h3>
            <div className="space-y-3">
              {priorityLabels.map((label, idx) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-gray-600">{label}</div>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-lg transition-all',
                        idx === 4 ? 'bg-red-500' :
                        idx === 3 ? 'bg-orange-500' :
                        idx === 2 ? 'bg-yellow-500' :
                        idx === 1 ? 'bg-blue-400' : 'bg-gray-400'
                      )}
                      style={{ width: `${(priorityCounts[idx] / maxPriorityCount) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 text-sm font-medium text-gray-900 text-right">{priorityCounts[idx]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Workload by Assignee */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-500" />
              Workload by Assignee
            </h3>
            <div className="space-y-3">
              {assigneeData.map((assignee: any) => (
                <div key={assignee.name} className="flex items-center gap-3">
                  <div className="w-28 text-sm text-gray-600 truncate">{assignee.name}</div>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-lg transition-all"
                      style={{ width: `${(assignee.count / maxAssigneeCount) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 text-sm font-medium text-gray-900 text-right">{assignee.count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Estimates */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Story Points</h3>
            <div className="flex items-center gap-8">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#10b981"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(completedEstimate / Math.max(totalEstimate, 1)) * 352} 352`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{completedEstimate}</p>
                    <p className="text-xs text-gray-500">of {totalEstimate}</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Completed</span>
                    <span className="font-medium text-green-600">{completedEstimate} pts</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${(completedEstimate / Math.max(totalEstimate, 1)) * 100}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Remaining</span>
                    <span className="font-medium text-gray-600">{totalEstimate - completedEstimate} pts</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-full bg-gray-400 rounded-full"
                      style={{ width: `${((totalEstimate - completedEstimate) / Math.max(totalEstimate, 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Current Cycle */}
        {currentCycle && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Current Cycle: {currentCycle.bh_name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{cycleIssues?.length || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Total Issues</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{cycleCompleted}</p>
                <p className="text-sm text-gray-500 mt-1">Completed</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">
                  {cycleIssues?.length ? Math.round((cycleCompleted / cycleIssues.length) * 100) : 0}%
                </p>
                <p className="text-sm text-gray-500 mt-1">Progress</p>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${cycleIssues?.length ? (cycleCompleted / cycleIssues.length) * 100 : 0}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{currentCycle.bh_startdate}</span>
                <span>{currentCycle.bh_enddate}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
